// Totals and formats of the project monthly report screens (mirrors backend utils/projectReport.util.ts).

import { KSA_CATEGORIES, ReportData, ReportTemplate } from '../types/projectReport';

const round3 = (n: number): number => Math.round(n * 1000) / 1000;
const sum = (values: Array<number | null | undefined>): number | null => {
  const nums = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return nums.length ? round3(nums.reduce((a, b) => a + b, 0)) : null;
};

export function contractTotal(template: ReportTemplate, amounts: ReportData['amounts']): number | null {
  return template === 'KSA' ? sum(KSA_CATEGORIES.map((c) => amounts[c])) : sum([amounts.main, amounts.variationOrders]);
}

/** Days from start to end, both included. */
export function durationDays(start: string | null, end: string | null): number | null {
  if (!start || !end || !/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  const days = Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;
  return days > 0 ? days : null;
}

/** Pending NCRs at month end: previous pending + issued - closed. */
export function pendingNcrs(previous: number | null, issued: number | null, closed: number | null): number | null {
  if (issued === null && closed === null) return previous;
  return Math.max((previous ?? 0) + (issued ?? 0) - (closed ?? 0), 0);
}

export const CURRENCY: Record<ReportTemplate, { code: string; decimals: number }> = {
  KSA: { code: 'SAR', decimals: 2 },
  KWT: { code: 'KWD', decimals: 3 },
};

export function formatAmount(v: number | null | undefined, template: ReportTemplate): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '';
  const d = CURRENCY[template].decimals;
  return v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

/** An amount typed by the user: digits, one point, a leading minus, commas ignored; null when empty or not a number. */
export function parseAmount(text: string): number | null {
  const t = String(text ?? '').replace(/,/g, '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? round3(n) : null;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** '2026-10' / '2026-10-01' -> 'October 2026' */
export function monthLabel(month: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(String(month || ''));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
}

/** The current month as YYYY-MM (local time). */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** 2026-10-28 -> 28 Oct 2026 */
export function dateLabel(v: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1].slice(0, 3)} ${m[1]}` : '';
}

/** Read a field of the report data by its path ('amounts.main'). */
export function getPath(data: unknown, path: string): unknown {
  return path.split('.').reduce<any>((o, k) => (o && typeof o === 'object' ? o[k] : undefined), data);
}

/** A copy of the report data with one field changed. */
export function setPath<T>(data: T, path: string, value: unknown): T {
  const copy: any = JSON.parse(JSON.stringify(data));
  const keys = path.split('.');
  let o = copy;
  for (const k of keys.slice(0, -1)) {
    if (!o[k] || typeof o[k] !== 'object') o[k] = {};
    o = o[k];
  }
  o[keys[keys.length - 1]] = value;
  return copy;
}

/** True when a SAP-filled field differs from what SAP gives (and SAP gives something). */
export function differsFromSap(value: unknown, sap: string | number | null | undefined): boolean {
  if (sap === null || sap === undefined || sap === '') return false;
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'number' && typeof sap === 'number') return Math.abs(value - sap) >= 0.0005;
  return String(value).trim() !== String(sap).trim();
}
